type SimFrequencyKey = "monthly" | "threeMonths" | "sixMonths" | "twelveMonths";

type FrequencyOption = {
  key: SimFrequencyKey;
  label: string;
};

type UpgradePlanEntry = {
  _id: string;
  name: string;
  price: string;
  frequency?: number | string;
  savings?: string;
  features?: unknown;
};

const FREQUENCY_ORDER: FrequencyOption[] = [
  { key: "monthly", label: "1 Month" },
  { key: "threeMonths", label: "3 Months" },
  { key: "sixMonths", label: "6 Months" },
  { key: "twelveMonths", label: "12 Months" },
];

export function normalizePrimaryDataTierKey(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.toLowerCase().replace(/\s+/g, "");
}

export function frequencyKeyFromMonths(value: unknown): SimFrequencyKey {
  const n =
    typeof value === "number"
      ? value
      : Number.parseInt(String(value ?? "").trim(), 10);
  switch (n) {
    case 1:
      return "monthly";
    case 3:
      return "threeMonths";
    case 6:
      return "sixMonths";
    case 12:
      return "twelveMonths";
    default:
      return "threeMonths";
  }
}

export function preloadedSimServiceFrequencyKeyToMonths(
  key: string | undefined,
): number | null {
  switch (key) {
    case "monthly":
      return 1;
    case "threeMonths":
      return 3;
    case "sixMonths":
      return 6;
    case "twelveMonths":
      return 12;
    default:
      return null;
  }
}

export function frequencySuffixFromKey(key: string): string {
  switch (key) {
    case "monthly":
      return "mo";
    case "threeMonths":
      return "3 mo";
    case "sixMonths":
      return "6 mo";
    case "twelveMonths":
      return "year";
    default:
      return "mo";
  }
}

export function parseCurrencyToNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const numeric = Number.parseFloat(value.replace(/[^\d.]/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
}

export function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

export function formatUsdCompact(value: number): string {
  if (!Number.isFinite(value)) return "$0";
  return Number.isInteger(value) ? `$${value.toFixed(0)}` : `$${value.toFixed(2)}`;
}

export function computeOriginalPrice(price: unknown, savings: unknown): string | null {
  const p = parseCurrencyToNumber(price);
  const s = parseCurrencyToNumber(savings);
  if (p == null || s == null || s <= 0) return null;
  return formatUsd(p + s);
}

export function computeOriginalPriceCompact(
  price: unknown,
  savings: unknown,
): string | null {
  const p = parseCurrencyToNumber(price);
  const s = parseCurrencyToNumber(savings);
  if (p == null || s == null || s <= 0) return null;
  return formatUsdCompact(p + s);
}

export function flattenPlanFeatures(features: unknown): string[] {
  if (!features || typeof features !== "object") return [];
  const result: string[] = [];

  const walk = (value: unknown, keyPrefix?: string) => {
    if (Array.isArray(value)) {
      value
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
        .forEach((item) => {
          result.push(keyPrefix ? `${keyPrefix}: ${item}` : item);
        });
      return;
    }
    if (value && typeof value === "object") {
      Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
        const cleanKey = k.trim();
        walk(v, cleanKey || keyPrefix);
      });
      return;
    }
    if (typeof value === "string" && value.trim()) {
      result.push(keyPrefix ? `${keyPrefix}: ${value.trim()}` : value.trim());
    }
  };

  walk(features);
  return result;
}

export function buildSingleNetworkFeatureLines(features: unknown): string[] {
  if (!features || typeof features !== "object") return [];
  const f = features as Record<string, any>;
  const lines: string[] = [];

  const setLine = (label: string, value: unknown) => {
    if (typeof value !== "string" || !value.trim()) return;
    lines.push(`${label}: ${value.trim()}`);
  };

  const primaryDataValue = f?.data?.Data;
  if (typeof primaryDataValue === "string" && primaryDataValue.trim()) {
    const gbMatch = primaryDataValue.match(/\b\d+\s*GB\b/i);
    const cleaned = gbMatch
      ? gbMatch[0].replace(/\s+/g, " ").trim()
      : primaryDataValue.trim();
    setLine("Primary Data", `${cleaned} on AT&T's 5G network`);
  }

  const speedAfterLimit = f?.throttling?.speedAfterLimit;
  if (typeof speedAfterLimit === "string" && speedAfterLimit.trim()) {
    const val =
      speedAfterLimit.includes("(Unlimited)") || speedAfterLimit.includes("Unlimited")
        ? speedAfterLimit.trim()
        : `${speedAfterLimit.trim()} (Unlimited)`;
    setLine("Speed After Priority Data", val);
  }

  const voice = f?.communication?.voice;
  const sms = f?.communication?.sms;
  if (
    (typeof voice === "string" && voice.trim()) ||
    (typeof sms === "string" && sms.trim())
  ) {
    const v = typeof voice === "string" ? voice.trim() : "";
    const s = typeof sms === "string" ? sms.trim() : "";
    const voiceSms = v && s ? (v === s ? v : `${v} / ${s}`) : v || s;
    if (voiceSms) setLine("Voice & SMS", voiceSms);
  }

  const longDistance = f?.international?.longDistance;
  if (Array.isArray(longDistance) && longDistance.length > 0) {
    setLine("International Calls", longDistance.join(", "));
  } else if (typeof longDistance === "string" && longDistance.trim()) {
    setLine("International Calls", longDistance.trim());
  }

  // GEO / primaryData line (e.g. "Data for APPs that require GEO location") — hidden on choose-service-plan for now.
  // if (typeof f?.primaryData === "string" && f.primaryData.trim()) {
  //   lines.push(f.primaryData.trim());
  // }

  return lines;
}

export function frequencyDisplayLabel(key: string): string {
  switch (key) {
    case "monthly":
      return "Monthly";
    case "threeMonths":
      return "3 Months";
    case "sixMonths":
      return "6 Months";
    case "twelveMonths":
      return "12 Months";
    default:
      return "Monthly";
  }
}

export function buildFrequencyOptionsForPlan(
  selectedPlanName: string,
  plans: UpgradePlanEntry[],
): FrequencyOption[] {
  if (!selectedPlanName.trim()) return [];

  const seen = new Set<string>();
  return plans
    .filter((plan) => plan.name === selectedPlanName)
    .map((plan) => {
      const key = frequencyKeyFromMonths(plan.frequency);
      return {
        key,
        label: frequencyDisplayLabel(key),
        rank: FREQUENCY_ORDER.findIndex((f) => f.key === key),
      };
    })
    .filter((row) => {
      if (seen.has(row.key)) return false;
      seen.add(row.key);
      return true;
    })
    .sort((a, b) => a.rank - b.rank)
    .map(({ key, label }) => ({ key, label }));
}

export function findUpgradePlanByNameAndFrequency(
  plans: UpgradePlanEntry[],
  selectedPlanName: string,
  frequencyKey: string,
): UpgradePlanEntry | null {
  return (
    plans.find(
      (plan) =>
        plan.name === selectedPlanName &&
        frequencyKeyFromMonths(plan.frequency) === frequencyKey,
    ) ?? null
  );
}
